import { supabase } from '../lib/supabase';

// Helper function to translate Supabase errors to Dutch
const translateError = (error) => {
  if (!error) return 'Er is een onbekende fout opgetreden';
  
  const errorMessage = error?.message || error?.msg || String(error);
  
  // Common Supabase error translations
  const errorMap = {
    'User already registered': 'Dit e-mailadres is al geregistreerd',
    'Invalid email': 'Ongeldig e-mailadres',
    'Password should be at least 6 characters': 'Wachtwoord moet minimaal 6 karakters bevatten',
    'Signup requires a valid password': 'Wachtwoord is verplicht',
    'Unable to validate email address': 'Kan e-mailadres niet valideren',
    'Email rate limit exceeded': 'Te veel pogingen. Probeer het later opnieuw',
    'Invalid login credentials': 'Ongeldige inloggegevens',
    'Email not confirmed': 'E-mailadres is nog niet bevestigd',
    'User not found': 'Gebruiker niet gevonden',
    'Network request failed': 'Netwerkfout. Controleer uw internetverbinding'
  };
  
  // Check for exact matches
  for (const [key, value] of Object.entries(errorMap)) {
    if (errorMessage?.includes(key)) {
      return value;
    }
  }
  
  // Return original message if no translation found
  return errorMessage || 'Er is een fout opgetreden bij het aanmaken van uw account';
};

export const authService = {
  async signUp(email, password, userData) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData?.fullName || `${userData?.firstName} ${userData?.lastName}`,
            user_type: userData?.userType || 'customer',
            phone: userData?.phone || '',
            postal_code: userData?.postalCode || '',
            company_name: userData?.companyName || '',
            kvk_number: userData?.kvkNumber || ''
          }
        }
      });
      
      if (error) {
        return { 
          data: null, 
          error: { 
            message: translateError(error),
            originalError: error 
          } 
        };
      }
      
      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { 
          message: translateError(error),
          originalError: error 
        } 
      };
    }
  },

  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signInWithGoogle() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/homepage`
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signInWithFacebook() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location?.origin}/homepage`
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      if (error) throw error;
      return { user, error: null };
    } catch (error) {
      return { user: null, error };
    }
  },

  async updateProfile(userId, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};
